import {
  bulkNehFromLlh,
  downloadGeodataFiles,
  getFromBase64Csib,
  getFromDc,
  GetFromDcResponse,
} from './generated';
import { Client } from './generated/client/types.gen';
import { useQuery, UseQueryResult } from '@tanstack/solid-query';
import { returnMappedOrThrow } from '@trimble/http-utils';
import {
  CalibrationFileInfo,
  CoordinateSystemData,
  CoordinateSystemFromDcFileResponse,
} from './coordinates-api-service.types';
import { mapCoordinateSystemData } from './coordinates-api-service.mappers';

export class CoordinatesApiService {
  constructor(private readonly client: Client) {}

  public getCoordinateDataFromDc(
    calibrationFileInfo: CalibrationFileInfo | undefined,
    initialData?: GetFromDcResponse
  ): UseQueryResult<CoordinateSystemData, Error> {
    return useQuery(() => ({
      initialData: initialData
        ? (mapCoordinateSystemData(initialData) ?? undefined)
        : undefined,
      queryKey: ['getCoordinateDataFromDc', calibrationFileInfo?.fileName],
      queryFn: async () => {
        let calibrationFileContents: string;
        try {
          calibrationFileContents = await (
            await fetch(calibrationFileInfo!.fileDownloadLink)
          ).text();
        } catch {
          throw new Error(
            `Error fetching calibration file: ${calibrationFileInfo!.fileName}.`
          );
        }

        const response = await getFromDc({
          body: calibrationFileContents,
          headers: {
            //eslint-disable-next-line @typescript-eslint/naming-convention
            'Content-Type': 'text/plain',
          },
          client: this.client,
        });

        return returnMappedOrThrow(
          response,
          'Error occured getting Coordinates data from calibration file',
          mapCoordinateSystemData
        );
      },
      refetchOnWindowFocus: false,
      enabled: !!calibrationFileInfo,
    }));
  }

  public fetchGeoDataFiles = async (
    coordinatesDataValue: CoordinateSystemFromDcFileResponse | undefined
  ): Promise<Map<string, ArrayBuffer>> => {
    return downloadGeoDataFilesFromResponse(this.client, coordinatesDataValue);
  };

  /**
   * Converts WGS-84 global longitude/latitude/height positions into Orientated
   * NEH coordinates for the supplied target coordinate system (e.g. the local
   * feature CRS urn). Used to place a feature stored as WGS-84 lon/lat into the
   * local scene frame its sibling features already use, recovering the Z
   * (height) that 2D geographic geometry lacks.
   *
   * @param llhPoints rows of [longitude, latitude, height] (WGS-84 degrees/metres).
   *   Height may be 0 when the source geometry is 2D.
   * @param fromCoordinateSystemId target coordinate system reference (the CRS
   *   the local features are expressed in).
   * @returns rows of [north, east, height] in the target coordinate system.
   */
  public async convertGlobalLlhToOrientatedNeh(
    llhPoints: ReadonlyArray<readonly [number, number, number]>,
    fromCoordinateSystemId: string
  ): Promise<Array<[number, number, number]>> {
    if (llhPoints.length === 0) {
      return [];
    }
    // The Coordinates API expects LLH rows ordered [latitude, longitude, height].
    const body = llhPoints.map(([lon, lat, height]) => [lat, lon, height]);
    const response = await bulkNehFromLlh({
      client: this.client,
      path: { nehType: 'orientated' },
      query: { fromCoordinateSystemId, fromType: 'referenceGlobal' },
      body,
    });

    if (response.error) {
      throw new Error(
        `Coordinates service rejected LLH->NEH conversion: ${JSON.stringify(response.error)}`
      );
    }

    const rows = (response.data as number[][] | undefined) ?? [];
    const heightIndex = 2;
    return rows.map(
      (row) =>
        [row[0], row[1], row[heightIndex] ?? 0] as [number, number, number]
    );
  }
}

export async function downloadGeoDataFilesFromResponse(
  client: Client,
  coordinatesData: CoordinateSystemFromDcFileResponse | undefined
): Promise<Map<string, ArrayBuffer>> {
  if (!coordinatesData?.coordinateSystem?.files) {
    return new Map<string, ArrayBuffer>();
  }

  const filePromises = Object.values(
    coordinatesData.coordinateSystem.files ?? {}
  )
    .filter((name) => !name.endsWith('.ggf'))
    .map(async (fileName) => {
      const response = await downloadGeodataFiles({
        query: { filename: fileName },
        client,
        parseAs: 'blob',
      });

      if (response.error) {
        throw new Error(`Failed to fetch geodata file: ${fileName}`);
      }

      return {
        name: fileName,
        buffer: await (response.data as Blob).arrayBuffer(),
      };
    });

  const namedBuffers = await Promise.all(filePromises);
  const geoDataFileMap = new Map<string, ArrayBuffer>();
  for (const { name, buffer } of namedBuffers) {
    geoDataFileMap.set(name, buffer);
  }
  return geoDataFileMap;
}

export async function fetchGeoDataFilesByCsib(
  client: Client,
  csib64: string
): Promise<Map<string, ArrayBuffer>> {
  const response = await getFromBase64Csib({
    //eslint-disable-next-line @typescript-eslint/naming-convention
    query: { csib_64: csib64 },
    client,
  });

  if (response.error) {
    throw new Error(
      `Failed to fetch coordinate system from CSIB: ${JSON.stringify(response.error)}`
    );
  }

  const coordinatesData = response.data as
    | CoordinateSystemFromDcFileResponse
    | undefined;

  return downloadGeoDataFilesFromResponse(client, coordinatesData);
}
